import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import VideoCard from '@/components/feature/VideoCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOMeta from '@/components/feature/SEOMeta';
import { getVideosByCategory, getCategoryName, categories } from '@/mocks/videos';

export default function CategoryPage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const [activeYear, setActiveYear] = useState<string>('all');
  const [activeRegion, setActiveRegion] = useState<string>('all');

  const categoryName = getCategoryName(slug || '');
  const videos = getVideosByCategory(slug || '');

  const years = useMemo(() => {
    const y = [...new Set(videos.map((v) => v.year))].sort((a, b) => b - a);
    return y;
  }, [videos]);

  const regions = useMemo(() => {
    return [...new Set(videos.map((v) => v.region))];
  }, [videos]);

  const filteredVideos = useMemo(() => {
    return videos.filter((v) => {
      if (activeYear !== 'all' && v.year !== Number(activeYear)) return false;
      if (activeRegion !== 'all' && v.region !== activeRegion) return false;
      return true;
    });
  }, [videos, activeYear, activeRegion]);

  const categoryTitle = `${categoryName}在线观看 - 国产${categoryName}免费在线观看`;
  const categoryDesc = `精选最新国产${categoryName}高清资源，每日更新热门国产${categoryName}，支持手机/电脑多端在线观看。涵盖热门${categoryName}、经典${categoryName}、最新${categoryName}，为您打造最佳国产${categoryName}在线观看体验。`;

  return (
    <>
      <SEOMeta
        title={categoryTitle}
        description={categoryDesc}
        keywords={`国产${categoryName}在线观看,最新${categoryName},热门${categoryName},高清${categoryName},免费${categoryName}在线观看`}
        canonicalUrl={`https://dywatch.github.io/category/${slug}`}
      />

      {/* BreadcrumbList Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://dywatch.github.io/' },
              { '@type': 'ListItem', position: 2, name: categoryName },
            ],
          }),
        }}
      />

      <Navbar />

      <main>
        <div className="px-4 md:px-6 py-6">
          {/* Breadcrumb */}
          <Breadcrumb
            items={[
              { label: t('breadcrumb.home'), href: '/' },
              { label: categoryName },
            ]}
          />

          {/* Title */}
          <div className="mt-4 mb-6">
            <h1 className="text-xl md:text-2xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
              {categoryName}在线观看
            </h1>
            <p className="text-sm text-foreground-600 mt-2">精选国产{categoryName}高清资源，每日更新，即点即播</p>
          </div>

          {/* Sidebar categories */}
          <div className="flex flex-wrap gap-2 mb-5">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200/70 border border-background-200/70'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6 p-4 bg-background-100 rounded-xl border border-background-200/70">
            {/* Year filter */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-foreground-600 shrink-0 whitespace-nowrap">年份：</span>
              <div className="flex gap-1.5 flex-wrap">
                <button
                  onClick={() => setActiveYear('all')}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                    activeYear === 'all'
                      ? 'bg-secondary-500 text-background-50'
                      : 'bg-background-50 text-foreground-700 hover:bg-background-200/70 border border-background-200/70'
                  }`}
                >
                  全部
                </button>
                {years.map((y) => (
                  <button
                    key={y}
                    onClick={() => setActiveYear(String(y))}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                      activeYear === String(y)
                        ? 'bg-secondary-500 text-background-50'
                        : 'bg-background-50 text-foreground-700 hover:bg-background-200/70 border border-background-200/70'
                    }`}
                  >
                    {y}
                  </button>
                ))}
              </div>
            </div>

            {/* Region filter */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-foreground-600 shrink-0 whitespace-nowrap">地区：</span>
              <div className="flex gap-1.5 flex-wrap">
                <button
                  onClick={() => setActiveRegion('all')}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                    activeRegion === 'all'
                      ? 'bg-secondary-500 text-background-50'
                      : 'bg-background-50 text-foreground-700 hover:bg-background-200/70 border border-background-200/70'
                  }`}
                >
                  全部
                </button>
                {regions.map((r) => (
                  <button
                    key={r}
                    onClick={() => setActiveRegion(r)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                      activeRegion === r
                        ? 'bg-secondary-500 text-background-50'
                        : 'bg-background-50 text-foreground-700 hover:bg-background-200/70 border border-background-200/70'
                    }`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Result count */}
          <p className="text-sm text-foreground-500 mb-4">
            共 <strong className="text-foreground-950">{filteredVideos.length}</strong> 部作品
          </p>

          {/* Video grid */}
          {filteredVideos.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4 pb-10" data-product-shop>
              {filteredVideos.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 bg-background-100 rounded-full flex items-center justify-center">
                <i className="ri-film-line text-foreground-400 text-2xl w-8 h-8 flex items-center justify-center"></i>
              </div>
              <p className="text-foreground-600">暂无符合条件的影视内容</p>
              <button
                onClick={() => { setActiveYear('all'); setActiveRegion('all'); }}
                className="mt-4 px-4 py-2 text-sm bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                重置筛选
              </button>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}